import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { querySuiteFilterCounts } from '@/features/suites/data/suites';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { buildInFilter, mergeFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  TableDropdownOption,
  TableStatusOption,
} from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { SuiteFilterCountsDto, SuiteListItemDto, SuiteRunStatus } from '@flowqa/shared';

type CountMap = Partial<Record<string, number>>;

type SuitesPageFilterOptionCountArgs = {
  enabled: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  search: string;
  selectedStatuses: SuiteRunStatus[];
  selectedSuites: string[];
  selectedTags: string[];
  startDay: string | null;
  endDay: string | null;
  suiteOptions?: TableDropdownOption[];
  statusOptions?: Array<TableStatusOption<SuiteRunStatus>>;
  tagOptions?: TableDropdownOption[];
};

type CountedSuitesPageFilterOptions = {
  countedSuiteOptions: TableDropdownOption[];
  countedStatusOptions: Array<TableStatusOption<SuiteRunStatus>>;
  countedTagOptions: TableDropdownOption[];
};

type SuitesPageFilterCountInput = Pick<
  SuitesPageFilterOptionCountArgs,
  'selectedStatuses' | 'selectedSuites' | 'selectedTags'
>;

type SuiteFilterCountGroups = {
  suites: GridFilters;
  statuses: GridFilters;
  tags: GridFilters;
};

export const buildSuitesPageFilterCountGroups = ({
  selectedStatuses,
  selectedSuites,
  selectedTags,
}: SuitesPageFilterCountInput): SuiteFilterCountGroups => ({
  suites: mergeFilters(
    buildInFilter('executionStatus', selectedStatuses),
    buildInFilter('tags', selectedTags),
  ),
  statuses: mergeFilters(buildInFilter('id', selectedSuites), buildInFilter('tags', selectedTags)),
  tags: mergeFilters(
    buildInFilter('executionStatus', selectedStatuses),
    buildInFilter('id', selectedSuites),
  ),
});

const sumStatusCounts = (counts: Partial<Record<string, number>> | undefined): number =>
  Object.values(counts ?? {}).reduce<number>((sum, value) => {
    if (value === undefined) return sum;
    return sum + value;
  }, 0);

const sumExecutionStatusCounts = (counts: Partial<Record<string, number>> | undefined): number => {
  const total = counts?.total;
  if (typeof total === 'number') return total;
  return sumStatusCounts(counts);
};

export const buildSuiteExecutionCountMap = (suites: readonly SuiteListItemDto[]): CountMap =>
  Object.fromEntries(
    suites.map((suite) => [suite.id, sumExecutionStatusCounts(suite.executionStatusCounts)]),
  ) as CountMap;

export const buildSuiteStatusCountMap = (suites: readonly SuiteListItemDto[]): CountMap => {
  const counts: CountMap = {};
  suites.forEach((suite) => {
    Object.entries(suite.executionStatusCounts ?? {}).forEach(([status, value]) => {
      if (status === 'total') return;
      counts[status] = (counts[status] ?? 0) + value;
    });
  });
  return counts;
};

export const buildSuiteTagExecutionCountMap = (suites: readonly SuiteListItemDto[]): CountMap => {
  const counts: CountMap = {};
  suites.forEach((suite) => {
    const suiteCount = sumExecutionStatusCounts(suite.executionStatusCounts);
    (suite.tags ?? []).forEach((tag) => {
      counts[tag] = (counts[tag] ?? 0) + suiteCount;
    });
  });
  return counts;
};

const applyCountsToOptions = (
  options: readonly TableDropdownOption[],
  counts: CountMap | undefined,
): TableDropdownOption[] => {
  if (counts === undefined) {
    return options.map((option) => ({ ...option }));
  }
  return options.map((option) => ({
    ...option,
    count: counts[option.value] ?? 0,
  }));
};

const applyCountsToStatusOptions = (
  options: ReadonlyArray<TableStatusOption<SuiteRunStatus>>,
  counts: CountMap | undefined,
): Array<TableStatusOption<SuiteRunStatus>> => {
  if (counts === undefined) {
    return options.map((option) => ({ ...option }));
  }
  const hasErroredOption = options.some((option) => option.value === 'errored');
  return options.map((option) => {
    const groupedErroredCount =
      option.value === 'failed' && !hasErroredOption ? (counts.errored ?? 0) : 0;
    return {
      ...option,
      count: (counts[option.value] ?? 0) + groupedErroredCount,
    };
  });
};

export const useSuitesPageFilterOptionCounts = ({
  enabled,
  selectedProjectId,
  selectedOrganisationId,
  search,
  selectedStatuses,
  selectedSuites,
  selectedTags,
  startDay,
  endDay,
  suiteOptions = [],
  statusOptions = [],
  tagOptions = [],
}: SuitesPageFilterOptionCountArgs): CountedSuitesPageFilterOptions => {
  const countGroups = useMemo(
    () =>
      buildSuitesPageFilterCountGroups({
        selectedStatuses,
        selectedSuites,
        selectedTags,
      }),
    [selectedStatuses, selectedSuites, selectedTags],
  );
  const optionCountKey = useMemo(
    () =>
      JSON.stringify({
        search,
        selectedStatuses,
        selectedSuites,
        selectedTags,
        startDay,
        endDay,
      }),
    [endDay, search, selectedStatuses, selectedSuites, selectedTags, startDay],
  );
  const isEnabled = enabled && selectedProjectId !== null;
  const countsQuery = useQuery<SuiteFilterCountsDto>({
    queryKey: queryKeys.dataGrid(
      gridPaths.suites,
      'filter-option-counts',
      selectedOrganisationId,
      selectedProjectId,
      optionCountKey,
    ),
    enabled: isEnabled,
    queryFn: async (): Promise<SuiteFilterCountsDto> => {
      if (selectedProjectId === null) {
        return { suites: {}, statuses: {}, tags: {} };
      }
      return await querySuiteFilterCounts(selectedProjectId, selectedOrganisationId, {
        search: search.trim() || undefined,
        metricsStartDay: startDay ?? undefined,
        metricsEndDay: endDay ?? undefined,
        filters: countGroups,
      });
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  return {
    countedSuiteOptions: applyCountsToOptions(suiteOptions, countsQuery.data?.suites),
    countedStatusOptions: applyCountsToStatusOptions(statusOptions, countsQuery.data?.statuses),
    countedTagOptions: applyCountsToOptions(tagOptions, countsQuery.data?.tags),
  };
};
